import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FiActivity, FiBriefcase, FiCode, FiCpu, FiDatabase, FiLayers, FiMapPin, FiTool } from "react-icons/fi";

import { AppWrap, MotionWrap } from "../../wrapper";
import { client, urlFor } from "../../client";
import { images } from "../../constants";
import "./Skills.scss";

const fallbackSkills = [
  { name: "React", localIcon: images.react },
  { name: "JavaScript", localIcon: images.javascript },
  { name: "TypeScript", localIcon: images.typescript },
  { name: "Sass", localIcon: images.sass },
  { name: "Redux", localIcon: images.redux },
  { name: "Node", localIcon: images.node },
  { name: "GraphQL", localIcon: images.graphql },
  { name: "Git", localIcon: images.git },
];

const fallbackExperiences = [
  {
    year: "2026",
    works: [{ name: "Frontend Interface Systems", company: "Product pages, dashboards, and portfolio experiences", location: "Remote / UAE" }],
  },
  {
    year: "2025",
    works: [{ name: "Product Interface Builds", company: "Responsive dashboards, content flows, and launch pages", location: "Remote" }],
  },
  {
    year: "2024",
    works: [{ name: "Web Experience Delivery", company: "Performance-focused websites and interactive UI", location: "Remote" }],
  },
];

const highlights = [
  { icon: <FiCpu />, label: "Frontend", value: "Component systems" },
  { icon: <FiActivity />, label: "Quality", value: "Responsive polish" },
  { icon: <FiBriefcase />, label: "Delivery", value: "Production handoff" },
];

const stackGroups = [
  {
    title: "Interface engineering",
    label: "Core UI",
    description: "Reusable views, stateful components, and frontend interaction patterns.",
    icon: <FiCode />,
    keywords: ["react", "javascript", "typescript", "redux", "vue"],
  },
  {
    title: "Styling systems",
    label: "Visual layer",
    description: "Responsive CSS architecture, design tokens, and layout systems that hold up across screens.",
    icon: <FiLayers />,
    keywords: ["css", "sass", "tailwind", "material", "matrial", "mui", "html"],
  },
  {
    title: "Content and data",
    label: "Integration",
    description: "Content, data, and integration patterns for editable frontend experiences.",
    icon: <FiDatabase />,
    keywords: ["sanity", "mongo", "node", "express", "graphql", "api"],
  },
  {
    title: "Build workflow",
    label: "Tooling",
    description: "Version control and delivery habits that keep implementation clean and shippable.",
    icon: <FiTool />,
    keywords: ["git", "github", "webpack", "vite", "npm", "bun"],
  },
];

const getSkillIcon = (skill) => {
  if (skill.localIcon) return skill.localIcon;
  if (skill.icon) return urlFor(skill.icon);
  return images.react;
};

const normalizeSkill = (value = "") =>
  value.toString().toLowerCase().replace(/[^a-z0-9]/g, "");

const getExperienceName = (name = "Frontend Developer") => {
  const normalized = normalizeSkill(name);
  if (normalized.includes("react") || normalized.includes("javascript") || normalized.includes("typescript")) {
    return "Frontend Developer";
  }
  return name;
};

const getSkillName = (skill) => {
  const name = skill.name || "Frontend";
  if (normalizeSkill(name) === "matrialui") return "Material UI";
  if (normalizeSkill(name) === "nodejs") return "Node JS";
  if (normalizeSkill(name) === "expressjs") return "Express JS";
  return name;
};

const getSkillDisplayName = (skill) => {
  const normalized = normalizeSkill(getSkillName(skill));

  if (normalized.includes("react") || normalized.includes("vue")) return "Interface Architecture";
  if (normalized.includes("javascript")) return "Interaction Logic";
  if (normalized.includes("typescript")) return "Type-safe UI";
  if (normalized.includes("redux")) return "State Management";
  if (normalized.includes("sass") || normalized.includes("tailwind")) return "Responsive Styling";
  if (normalized === "css") return "Styling Systems";
  if (normalized === "html") return "Semantic Structure";
  if (normalized.includes("material") || normalized.includes("mui")) return "Component Libraries";
  if (normalized.includes("sanity") || normalized.includes("cms")) return "Content Modeling";
  if (normalized.includes("graphql") || normalized.includes("api")) return "Data Interfaces";
  if (normalized.includes("node") || normalized.includes("express") || normalized.includes("mongo")) return "Integration Support";
  if (normalized.includes("git")) return "Version Control";

  return getSkillName(skill);
};

const getStackGroups = (skills) => {
  const used = new Set();

  const groups = stackGroups.map((group) => {
    const items = skills.filter((skill, index) => {
      if (used.has(index)) return false;
      const normalized = normalizeSkill(getSkillName(skill));
      const matches = group.keywords.some((keyword) => normalized.includes(normalizeSkill(keyword)));
      if (matches) used.add(index);
      return matches;
    });

    return { ...group, items };
  });

  const remaining = skills.filter((_, index) => !used.has(index));
  if (remaining.length) {
    groups.push({
      title: "Additional skills",
      label: "Support",
      description: "Useful supporting skills that round out frontend implementation work.",
      icon: <FiTool />,
      items: remaining,
    });
  }

  return groups.filter((group) => group.items.length);
};

const Skills = () => {
  const [experiences, setExperiences] = useState([]);
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    client.fetch('*[_type == "experiences"] | order(year desc)').then((data) => setExperiences(data || []));
    client.fetch('*[_type == "skills"]').then((data) => setSkills(data || []));
  }, []);

  const visibleSkills = skills.length ? skills : fallbackSkills;
  const visibleExperiences = experiences.length ? experiences : fallbackExperiences;
  const groupedSkills = getStackGroups(visibleSkills);

  return (
    <div className="skl">
      <div className="app__section-head">
        <motion.div
          className="app__section-title"
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
        >
          <span className="app__kicker">
            <em>04</em>Capabilities
          </span>
          <h2>
            Capabilities with delivery <em>discipline.</em>
          </h2>
        </motion.div>

        <motion.p
          className="app__section-lede"
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
        >
          The right engineering approach for each interface: fast, stable,
          maintainable, and flexible enough for real product teams.
        </motion.p>
      </div>

      <motion.div
        className="skl__highlights"
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        viewport={{ once: true }}
      >
        {highlights.map((item) => (
          <div className="skl__highlight" key={item.label}>
            <span className="skl__highlight-icon">{item.icon}</span>
            <div>
              <small>{item.label}</small>
              <strong>{item.value}</strong>
            </div>
          </div>
        ))}
      </motion.div>

      <div className="skl__body">
        <div className="skl__stack">
          <div className="skl__panel-head">
            <span>Frontend practice</span>
            <strong>{visibleSkills.length} skills</strong>
          </div>

          <div className="skl__groups">
            {groupedSkills.map((group, groupIndex) => (
              <motion.article
                className="skl__group"
                key={group.title}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: groupIndex * 0.07, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, amount: 0.15 }}
              >
                <div className="skl__group-head">
                  <span className="skl__group-icon">{group.icon}</span>
                  <small>{group.label}</small>
                </div>
                <h3>{group.title}</h3>
                <p>{group.description}</p>

                <div className="skl__chips">
                  {group.items.map((skill, index) => (
                    <span key={`${group.title}-${getSkillName(skill)}-${index}`}>
                      <img src={getSkillIcon(skill)} alt="" loading="lazy" />
                      {getSkillDisplayName(skill)}
                    </span>
                  ))}
                </div>
              </motion.article>
            ))}
          </div>

          <div className="skl__strip" aria-label="Daily tools">
            <span className="skl__strip-label">Daily tools</span>
            <div className="skl__strip-icons">
              {visibleSkills.slice(0, 12).map((skill, index) => (
                <span key={`${getSkillName(skill)}-strip-${index}`} title={getSkillDisplayName(skill)}>
                  <img src={getSkillIcon(skill)} alt={getSkillDisplayName(skill)} loading="lazy" />
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="skl__timeline-panel">
          <div className="skl__panel-head">
            <span>Experience</span>
            <strong>Recent focus</strong>
          </div>

          <div className="skl__timeline">
            {visibleExperiences.map((exp, index) => (
              <motion.div
                className="skl__entry"
                key={`${exp.year}-${index}`}
                initial={{ opacity: 0, x: 26 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true }}
              >
                <span className="skl__year">{exp.year}</span>
                <div className="skl__entry-works">
                  {exp.works.map((work) => (
                    <div className="skl__work" key={`${exp.year}-${work.name}`}>
                      <h3>{getExperienceName(work.name)}</h3>
                      <p>{work.company}</p>
                      {work.location && (
                        <small>
                          <FiMapPin aria-hidden="true" /> {work.location}
                        </small>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppWrap(
  MotionWrap(Skills, "app__skills"),
  "skills",
  "app__band--paper"
);
